// planUtils.js - utility for plan info and badge logic
// Plan limits and display names are loaded from chattitude_admin.plans via GET /api/account/plans.
// Call setPlansFromApi(plansArray) when plans are fetched (e.g. on login). Fallbacks used until then.

/** @type {{ byCode: Record<string, { monthlyConversations: number, name: string }> } | null} */
let plansCache = null;

/**
 * Normalize plan type to plan code (B, S, P, U) for lookup.
 * @param {string} planType - Plan type (e.g. 'B', 'S', 'starter', 'Starter')
 * @returns {string} - Normalized code 'B'|'S'|'P'|'U' or lowercased planType
 */
function normalizePlanKey(planType) {
  if (!planType) return 'B';
  const u = String(planType).toUpperCase();
  if (u === 'B') return 'B';
  if (u === 'S') return 'S';
  if (u === 'P') return 'P';
  if (u === 'U') return 'U';
  const lower = String(planType).toLowerCase();
  if (lower === 'beta') return 'B';
  if (lower === 'starter') return 'S';
  if (lower === 'pro') return 'P';
  if (lower === 'ultimate') return 'U';
  return u;
}

/**
 * Populate plan limits from the database (GET /api/account/plans response).
 * Call this when plans are fetched so getPlanLimit/getPlanDisplayName use DB values.
 * @param {Array<{ value?: string, planCode?: string, name?: string, monthlyConversations?: number }>} plansArray - plans from API (each has value or planCode, name, monthlyConversations)
 */
export function setPlansFromApi(plansArray) {
  if (!Array.isArray(plansArray) || plansArray.length === 0) {
    plansCache = null;
    return;
  }
  const byCode = {};
  for (const p of plansArray) {
    const code = (p.value || p.planCode || '').toString().toUpperCase();
    if (!code) continue;
    const monthlyConversations = p.monthlyConversations != null ? Number(p.monthlyConversations) : null;
    const name = p.name != null ? String(p.name) : code;
    byCode[code] = {
      monthlyConversations: Number.isFinite(monthlyConversations) ? monthlyConversations : null,
      name
    };
  }
  plansCache = { byCode };
}

/** Fallback limit when plans have not been loaded from API (avoid hardcoding DB values here). */
const FALLBACK_LIMIT = 100;

/**
 * Get monthly conversation (chat) limit for a plan. Uses DB plans when setPlansFromApi has been called.
 * @param {string} planType - Plan type (e.g. 'B', 'S', 'starter')
 * @returns {number} - Monthly conversations limit from plans table or fallback
 */
export function getPlanLimit(planType) {
  const code = normalizePlanKey(planType);
  if (plansCache?.byCode?.[code]?.monthlyConversations != null) {
    return plansCache.byCode[code].monthlyConversations;
  }
  return FALLBACK_LIMIT;
}

/**
 * Get display name for a plan. Uses DB plans when setPlansFromApi has been called.
 * @param {string} planType - Plan type (e.g. 'B', 'S', 'starter')
 * @returns {string} - Plan name from plans table or fallback
 */
export function getPlanDisplayName(planType) {
  const code = normalizePlanKey(planType);
  if (plansCache?.byCode?.[code]?.name) {
    return plansCache.byCode[code].name;
  }
  const names = { B: 'Beta', S: 'Starter', P: 'Pro', U: 'Ultimate' };
  return names[code] || 'Beta';
}

export function getPlanBadge(percentage) {
  if (percentage >= 90) {
    return { label: 'Limit Reached', color: 'error' };
  } else if (percentage >= 75) {
    return { label: 'Warning', color: 'warning' };
  } else {
    return { label: 'Good', color: 'success' };
  }
}
